import React, { useRef, useState, useLayoutEffect, useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { Measure as MeasureType, ActiveChord, TimeSignature, AppMode } from '../types';
import MeasureGrid from './MeasureGrid';
import { computeOptimalGigFontSize } from '../utils/gigAutoScale';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface ChartGridAdvancedProps {
  mode: AppMode;
  chart: MeasureType[];
  activeChord: ActiveChord | null;
  currentMeasure: number | null;
  currentChordIndex: number | null;
  startMeasureIndex: number;
  invalidChords: Record<string, string | null>;
  clipboard: (string | null)[] | null;
  onSetStartMeasure: (index: number) => void;
  onChordClick: (measureIndex: number, chordIndex: number) => void;
  onSubdivisionChange: (measureIndex: number, newSubdivision: number) => void;
  onPlayChord: (chord: string) => void;
  onCopyMeasure: (measureIndex: number) => void;
  onPasteMeasure: (measureIndex: number) => void;
  onChordHover: (chord: string, element: HTMLElement) => void;
  onChordLeave: () => void;
  onLyricChange: (measureIndex: number, newLyric: string) => void;
  timeSignature: TimeSignature;
  lyricsFontSize?: number;
  gigChordScale?: number;
  inspectorMode?: 'piano' | 'guitar';
  inspectorEnabled?: boolean;
  chartHasFine?: boolean;
  chartHasToCoda?: boolean;
}

const ChartGridAdvanced: React.FC<ChartGridAdvancedProps> = ({
  mode,
  chart,
  activeChord,
  currentMeasure,
  currentChordIndex,
  startMeasureIndex,
  invalidChords,
  clipboard,
  onSetStartMeasure,
  onChordClick,
  onSubdivisionChange,
  onPlayChord,
  onCopyMeasure,
  onPasteMeasure,
  onChordHover,
  onChordLeave,
  onLyricChange,
  timeSignature,
  lyricsFontSize,
  gigChordScale = 1.0,
  inspectorMode = 'piano',
  inspectorEnabled = false,
  chartHasFine = false,
  chartHasToCoda = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  const [gigFontSize, setGigFontSize] = useState(24);
  const [isLayoutReady, setIsLayoutReady] = useState(false);

  // Calculate layout for measures - always 4 columns for gig mode
  const layout = chart.map((_, index) => ({
    i: index.toString(),
    x: index % 4,           // 4 columns
    y: Math.floor(index / 4), // Row calculation
    w: 1,                   // Width: 1 grid unit
    h: 1,                   // Height: 1 grid unit
    static: true,           // No dragging/resizing for chord charts
    isDraggable: false,
    isResizable: false
  }));

  // Responsive breakpoints - iPad Pro is 1024px, ensure it gets 4 columns
  // Lower md threshold to 900px so iPad Pro (1024px) falls into md/lg with 4 columns
  const breakpoints = { lg: 1200, md: 900, sm: 600, xs: 400, xxs: 0 };
  const cols = { lg: 4, md: 4, sm: 4, xs: 4, xxs: 4 }; // Always 4 columns

  // Track both window height and container width for auto-scaling
  useLayoutEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    // Capture initial container width after mount
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
    return () => window.removeEventListener('resize', handleResize);
  }, []);  

  // Ensure grid is initialized after container is measured
  useLayoutEffect(() => {
    // Use requestAnimationFrame to ensure DOM layout is complete
    // This gives WidthProvider time to measure the container
    requestAnimationFrame(() => {
      setIsLayoutReady(true);
    });
  }, []);

  // Calculate layout and sizing based on latest windowHeight
  const headerHeight = 140;
  const footerHeight = 80;
  const containerPadding = 16; // Reduced from 32 to match new padding
  const gridGap = 4; // Reduced from 8 to match new margin
  const containerHeight = windowHeight - headerHeight - footerHeight;
  const numRows = Math.ceil(chart.length / 4);
  const availableHeight = containerHeight - containerPadding;
  const totalGapHeight = (numRows - 1) * gridGap;
  const availableForMeasures = availableHeight - totalGapHeight;
  const minRowHeight = 90;
  const maxRowHeight = 220;
  const calculatedRowHeight = availableForMeasures / numRows;
  const rowHeight = Math.max(minRowHeight, Math.min(calculatedRowHeight, maxRowHeight));
  // Auto-scale font size: find largest size that fits longest chord in column width
  const numCols = 4;
  const colGapTotal = (numCols - 1) * gridGap; // gaps between columns
  const colPaddingTotal = 2 * 8; // containerPadding [8,8] left+right
  const columnWidth = Math.max(60, (containerWidth - colPaddingTotal - colGapTotal) / numCols);

  const autoFontSize = useMemo(
    () => computeOptimalGigFontSize(chart, columnWidth, rowHeight),
    [chart, columnWidth, rowHeight]
  );

  // Update gigFontSize whenever auto-calculated size changes
  useLayoutEffect(() => {
    setGigFontSize(autoFontSize);
  }, [autoFontSize]);

  // ...existing code...

  return (
    <div 
      ref={containerRef} 
      className="w-full max-w-6xl mx-auto"
      style={{ height: `${containerHeight}px` }}
    >
      {!isLayoutReady ? (
        <div className="flex items-center justify-center h-full">
          <div className="text-cyan-400 font-mono text-sm animate-pulse">Loading...</div>
        </div>
      ) : (
        <ResponsiveGridLayout
          key="adaptive-grid"
          layouts={{ lg: layout, md: layout, sm: layout, xs: layout, xxs: layout }}
          breakpoints={breakpoints}
          cols={cols}
          rowHeight={rowHeight}
          isDraggable={false}
          isResizable={false}
          useCSSTransforms={true}
          margin={[4, 4]} // Minimal 4px gap
          containerPadding={[8, 8]} // Minimal 8px padding
          compactType={null}
          preventCollision={true}
          verticalCompact={false}
        >
          {chart.map((measure, index) => (
            <div 
              key={index.toString()}
              style={{
                height: `${rowHeight}px`,
                width: '100%',
                overflow: 'hidden' // Prevent content from spilling out
              }}
            >
              <div style={{ height: '100%', width: '100%' }}>
                <MeasureGrid
                  mode={mode}
                  measure={measure}
                  measureIndex={index}
                  activeChord={activeChord}
                  isPlaying={currentMeasure === index}
                  playingChordIndex={currentMeasure === index ? currentChordIndex : null}
                  isStartMeasure={startMeasureIndex === index}
                  onSetStartMeasure={onSetStartMeasure}
                  onChordClick={onChordClick}
                  onSubdivisionChange={onSubdivisionChange}
                  onPlayChord={onPlayChord}
                  onCopyMeasure={onCopyMeasure}
                  onPasteMeasure={onPasteMeasure}
                  onChordHover={onChordHover}
                  onChordLeave={onChordLeave}
                  onLyricChange={onLyricChange}
                  timeSignature={timeSignature}
                  invalidChords={invalidChords}
                  clipboard={clipboard}
                  gigFontSize={gigFontSize}
                  lyricsFontSize={lyricsFontSize}
                  gigChordScale={gigChordScale}
                  containerHeight={rowHeight}
                  inspectorMode={inspectorMode}
                  inspectorEnabled={inspectorEnabled}
                  chartHasFine={chartHasFine}
                  chartHasToCoda={chartHasToCoda}
                  previousMeasureHasEndRepeat={index > 0 ? !!chart[index - 1]?.annotations?.endRepeat : false}
                />
              </div>
            </div>
          ))}
        </ResponsiveGridLayout>
      )}
    </div>
  );
};

export default ChartGridAdvanced;